import React, { useState } from 'react';
import { CoverageProps, ProductItemProps } from '../../../../models/Product';
import ContainerPage from '../../../../components/ContainerPage';
import HeadTitle from '../../../../components/HeadTitle';
import { Page, useCheckUpRouter } from '../../router/context';
import { ProductSlots } from '../../components';
import { cancelAppointment, saveSchedule, SlotHour } from '../../../../modules/slots';
import { EligibilityPlanProps } from '../../../../contexts/PaymentContext';
import { Unit } from '../../../../components/ServiceItem';
import SystemUnavailable from '../../../../components/SystemUnavailable';
import { SystemUnavailableError } from '../../../../errors';
import ModalTimeNotAvailable from '../../../../components/ModalTimeNotAvailable';
import { SlotUnavailableError } from '../../../../errors/SlotUnavailableError';
import { Patient } from '../../../../models/Patient';
import { Button, LoadingOverlay } from '../../../../components';
import { toast } from 'react-toastify';

export type ProductSlotsPageProps = {
  products: ProductItemProps[];
  patient: Patient;
  elegibility: EligibilityPlanProps;
  firstAppointmentDate: string;
  isFirstAppointment: boolean;
};

function formatSchedules(
  product: ProductItemProps,
  coverage: CoverageProps,
  unit: Unit,
  elegibility: EligibilityPlanProps,
  slot: SlotHour,
  requestId?: number
) {
  return [
    {
      productId: product.id,
      itemId: coverage.item_SGH_id,
      careType: coverage.care_type,
      // careType: /*coverage.care_type*/ 'PRESENTIAL',
      productName: 'Check-up Digital - ' + product.name,
      catalogItemId: coverage.id,
      scheduleType: coverage.category,
      requestId,
      unit: {
        unitId: unit.id_sgh,
        name: unit.name,
        state: unit.state,
        address: unit.address,
        city: unit.city,
        number: unit.address_number,
        additionalInfo: '',
        neighborhood: unit.neighborhood,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        catalogUnitId: unit.id!
      },
      payment: {
        planCode: elegibility.cod_plano,
        nome: elegibility.nome,
        healthInsuranceId: String(elegibility.id_convenio),
        type: product.is_b2b ? 'B2B' : 'PARTICULAR'
      },
      selectedSlotOptions: [slot]
    }
  ];
}

export function ProductSlotsPage({
  products,
  patient,
  elegibility,
  firstAppointmentDate,
  isFirstAppointment
}: ProductSlotsPageProps) {
  const router = useCheckUpRouter();
  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = useState(false);
  const [isModalSlotUnavailableVisible, setIsModalSlotUnavailableVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [slots, setSlots] = useState<Record<string, SlotHour>>({});
  const [slotSelected, setSlotSelected] = useState<SlotHour>();
  const [productSelected, setProductSelected] = useState(products[0]?.id || undefined);
  const [appointments, setAppointments] = useState<
    Record<
      string,
      {
        token?: string;
        slot?: SlotHour;
        patientName: string;
        productName: string;
        noSlots?: boolean;
      }
    >
  >({});

  const handleError = (error: Error) => {
    setIsModalSlotUnavailableVisible(true);
  };

  const selectNextProduct = (product: ProductItemProps) => {
    if (products.length > 1) {
      const nextIndex = products.indexOf(product) + 1;
      setProductSelected(products[nextIndex]?.id || undefined);
    }
  };

  const schedule = async (product: ProductItemProps, slot: SlotHour) => {
    const [coverage] = product.coverages ?? [];
    const [unit] = coverage.units ?? [];

    const { token } = await saveSchedule({
      patient: patient,
      schedules: formatSchedules(product, coverage, unit, elegibility, slot)
    });

    if (token) {
      setAppointments({
        ...appointments,
        [product.id]: {
          slot: slot,
          token,
          patientName: `${patient.firstName} ${patient.lastName}`,
          productName: product.name
        }
      });

      selectNextProduct(product);
    }
  };

  const reschedule = async (product: ProductItemProps, slot: SlotHour) => {
    const appointment = appointments[product.id];
    if (!appointment.token) return;
    await cancelAppointment(patient.document, appointment.token);
    delete appointments[product.id];
    setAppointments({ ...appointments });
    await schedule(product, slot);
  };

  const handleSlot = async (product: ProductItemProps, slot: SlotHour) => {
    setLoadingSchedule(true);
    try {
      if (!appointments[product.id]?.token) await schedule(product, slot);
      else await reschedule(product, slot);
      return true;
    } catch (error: any) {
      console.log('Error trying to schedule/reschedule', error);

      delete appointments[product.id];
      setAppointments({
        ...appointments,
        [product.id]: {
          patientName: `${patient.firstName} ${patient.lastName}`,
          productName: product.name,
          noSlots: true
        }
      });

      selectNextProduct(product);

      handleError(error);
    } finally {
      setLoadingSchedule(false);
    }
    return false;
  };

  const toggleSlot = (product: ProductItemProps, slot?: SlotHour) => {
    if (!slot) {
      delete slots[product.id];
    } else {
      slots[product.id] = slot;
    }
    setSlotSelected(slot);
    setSlots({ ...slots });
  };

  const getExcludedHours = (product: ProductItemProps): { date: string; hour: string }[] => {
    const excluded: string[] = [];

    Object.entries(slots)
      .filter(([key]) => key !== product.id)
      .forEach(([_, slot]) => {
        excluded.push(slot.date + 'T' + slot.hour);
      });

    Object.entries(appointments)
      .filter(([key]) => key !== product.id)
      .forEach(([_, schedule]) => {
        if (schedule?.slot?.date && schedule?.slot?.hour)
          excluded.push(schedule?.slot?.date + 'T' + schedule?.slot?.hour);
      });

    return excluded.map(date => ({
      date: date.substring(0, 10),
      hour: date.substring(11, 19)
    }));
  };

  const wasAllAppointmentsScheduled = React.useMemo(() => {
    return products.every(item => {
      if (appointments[item.id]?.noSlots) return true;
      if (appointments[item.id]?.token) return true;
      return false;
    });
  }, [appointments]);

  const submit = async () => {
    if (products.length === 1 && slotSelected) handleSlot(products[0], slotSelected);

    if (products.length > 1 && wasAllAppointmentsScheduled) {
      const scheduledAppointments = Object.values(appointments).filter(item => item.token);

      router.navigate(Page.APPOINTMENT_CONFIRMATION, {
        schedules: scheduledAppointments,
        elegibility
      });
    }
  };

  const renderFooterButton = () => {
    if (products.length === 1 || wasAllAppointmentsScheduled)
      return (
        <Button onClick={submit} disabled={Object.keys(appointments).length === 0 && !slotSelected} title="Continuar" />
      );
    return undefined;
  };

  React.useEffect(() => {
    if (products.length === 1) {
      const firstProduct = products[0];

      if (appointments[firstProduct.id]?.token) {
        const schedules = Object.values(appointments).filter(item => item.token);

        router.navigate(Page.APPOINTMENT_CONFIRMATION, {
          schedules,
          elegibility
        });
      }
    }
  }, [wasAllAppointmentsScheduled]);

  return (
    <ContainerPage childrenFooter={renderFooterButton()}>
      <HeadTitle title="Data e horário" goBack={() => router.goBack({ page: Page.PATIENT_FORM })} />

      <div>
        {products.map((product, index) => (
          <ProductSlots
            key={product.id}
            product={product}
            fixed={products.length === 1 || !!appointments[product.id]?.token}
            visible={
              appointments[product.id]?.token ? false : !productSelected ? false : productSelected === product.id
            }
            exclude={getExcludedHours(product)}
            check={Boolean(appointments[product.id]?.token)}
            onSubmit={async slot => await handleSlot(product, slot)}
            onValueChange={slot => toggleSlot(product, slot)}
            onError={handleError}
            showScheduleButton={products.length > 1}
            toggleVisible={() => {
              if (productSelected === product.id) setProductSelected(undefined);
              else setProductSelected(product.id);
            }}
            hour={appointments[product.id]?.slot}
            firstAppointmentDate={firstAppointmentDate}
            isFirstAppointment={isFirstAppointment}
            handleWithoutSlots={() => {
              setAppointments({
                ...appointments,
                [product.id]: {
                  patientName: `${patient.firstName} ${patient.lastName}`,
                  productName: product.name,
                  noSlots: true
                }
              });
            }}
          />
        ))}
      </div>
      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
      />

      <ModalTimeNotAvailable
        visible={isModalSlotUnavailableVisible}
        onClose={() => setIsModalSlotUnavailableVisible(false)}
      />
      <LoadingOverlay
        loading={loading || loadingSchedule}
        label={loadingSchedule ? 'Realizando agendamento' : undefined}
      />
    </ContainerPage>
  );
}
